import { useState } from "react";
import { colors, fonts, classifyGhlTag } from "../../styles/theme";
import { updateContact } from "../../services/api";

export default function TagsSection({ contact, onContactUpdate }) {
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const crmTags = contact.tags || [];

  const handleAddTag = async () => {
    const tag = newTag.trim().toLowerCase();
    if (!tag || saving) return;
    if (crmTags.some((t) => t.toLowerCase() === tag)) {
      setNewTag("");
      return;
    }
    setSaving(true);
    try {
      const updatedTags = [...crmTags, tag];
      const updated = await updateContact(contact.id, { email: contact.email, tags: updatedTags });
      contact.tags = updatedTags;
      if (onContactUpdate) onContactUpdate(updated);
      setNewTag("");
    } catch (err) {
      alert(`Failed to add tag: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (saving) return;
    setSaving(true);
    try {
      const updatedTags = crmTags.filter((t) => t !== tagToRemove);
      const updated = await updateContact(contact.id, { email: contact.email, tags: updatedTags });
      contact.tags = updatedTags;
      if (onContactUpdate) onContactUpdate(updated);
    } catch (err) {
      alert(`Failed to remove tag: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveGhlTag = async (tagToRemove) => {
    if (saving) return;
    setSaving(true);
    try {
      const updatedGhlTags = (contact.ghlTags || []).filter((t) => t !== tagToRemove);
      const updated = await updateContact(contact.id, { email: contact.email, ghlTags: updatedGhlTags });
      contact.ghlTags = updatedGhlTags;
      if (onContactUpdate) onContactUpdate(updated);
    } catch (err) {
      alert(`Failed to remove tag: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const contentTags = (contact.ghlTags || []).filter(
    (t) => classifyGhlTag(t) === "content",
  );
  const ghlContactTags = (contact.ghlTags || []).filter(
    (t) => classifyGhlTag(t) === "contact",
  );

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Insights-managed tags */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 6,
          }}
        >
          Insights Tags
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
          {crmTags.length === 0 && (
            <span style={{ fontSize: 11, color: colors.textSecondary }}>No tags</span>
          )}
          {crmTags.map((tag) => (
            <span
              key={tag}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                padding: "3px 6px",
                borderRadius: 3,
                fontFamily: fonts.body,
                background: "rgba(59,130,246,0.06)",
                color: "#3b82f6",
                border: "1px solid rgba(59,130,246,0.2)",
              }}
            >
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                disabled={saving}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 12,
                  height: 12,
                  border: "none",
                  background: "transparent",
                  color: "#3b82f6",
                  cursor: saving ? "wait" : "pointer",
                  padding: 0,
                  fontSize: 12,
                  lineHeight: 1,
                  opacity: 0.5,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.5")}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
            placeholder="Add tag..."
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 11,
              fontFamily: fonts.body,
              border: `1px solid ${colors.border}`,
              borderRadius: 4,
              background: colors.bg,
              color: colors.text,
              outline: "none",
            }}
          />
          <button
            onClick={handleAddTag}
            disabled={!newTag.trim() || saving}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: 4,
              border: "none",
              background: newTag.trim() ? colors.accent : colors.bgActive,
              color: newTag.trim() ? "#ffffff" : colors.textSecondary,
              cursor: newTag.trim() ? "pointer" : "default",
              flexShrink: 0,
              fontSize: 14,
            }}
          >
            +
          </button>
        </div>
      </div>

      {/* Content tags (GHL) */}
      {contentTags.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 10,
              color: colors.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 6,
            }}
          >
            Content Tags
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {contentTags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 10,
                  padding: "3px 6px",
                  borderRadius: 3,
                  fontFamily: fonts.body,
                  background: "rgba(139,92,246,0.06)",
                  color: "#8b5cf6",
                  border: "1px solid rgba(139,92,246,0.2)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Contact tags (GHL) */}
      {ghlContactTags.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 10,
              color: colors.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 6,
            }}
          >
            Contact Tags
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {ghlContactTags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 10,
                  padding: "3px 6px",
                  borderRadius: 3,
                  fontFamily: fonts.body,
                  background: "rgba(99,102,241,0.06)",
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {tag}
                <button
                  onClick={() => handleRemoveGhlTag(tag)}
                  disabled={saving}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 12,
                    height: 12,
                    border: "none",
                    background: "transparent",
                    color: colors.textSecondary,
                    cursor: saving ? "wait" : "pointer",
                    padding: 0,
                    fontSize: 12,
                    lineHeight: 1,
                    opacity: 0.5,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.5")}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
